import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
import { SAMPLE_REPORT_PUBLISHED, SAMPLE_REPORT_LINK_TEXT } from '@/pages/sampleReportContent';
import { CrossPageAnalysis } from '@/components/CrossPageAnalysis';
import { PLAN_PRICES, ADDON_PRICES } from '@/config/pricing';
import PlanCards from '@/components/PlanCards';
import { Scan, CheckCircle, ArrowRight, Upload, BarChart3, FileText, Brain, Target, Eye, Smartphone, ShieldCheck, Layers, Network, Compass } from 'lucide-react';
import { SEO_P1_LIVE } from '@/config/seoPreview';
import AuditCheckoutPopup from '@/components/AuditCheckoutPopup';
import ScoreRing from '@/components/ds/ScoreRing';
import DimensionBars from '@/components/ds/DimensionBars';
import FeaturePreview from '@/components/ds/FeaturePreview';
import AccordionItem from '@/components/ds/AccordionItem';
import { DEMO_AUDIT } from '@/pages/homeLandingContent';
import '@/styles/forms.css';
import '@/styles/landing.css';

/**
 * /ux-auditor/ — rebuilt on the artboard, 2026-08-19.
 *
 * ============================================================================
 * WHAT THE ARTBOARD SUPPLIED, AND WHAT IT DID NOT
 * ============================================================================
 *
 * Source: the UXAuditPro UX Auditor export, markup read from line 393. The
 * artboard is ground truth for STRUCTURE; the shipped system is ground truth
 * for type, colour and spacing (ruling of 2026-08-19).
 *
 * EVERY SECTION OF THE LIVE PAGE SURVIVES. Hero, How It Works, the six
 * dimensions, Methodology, Pricing, FAQ and the closing CTA are all still here
 * with their headings and copy intact. The artboard supplied the hero report
 * card and the card treatments; it did not get to delete anything.
 *
 * ── OMITTED, WITH WHAT EACH WOULD NEED ──────────────────────────────────────
 *
 * 1. THE LOGO WALL — "Loved by over 500+ product teams and agencies!" over
 *    Northgate, Vessel, Marketly, Apiarium and Datalyte. Omitted twice over:
 *    the owner ruled the 500+ claim out, and the five names are third parties
 *    who are not customers. A claim about a named third party is the one thing
 *    removed without asking, because it carries legal exposure rather than
 *    merely being wrong.
 *    WOULD NEED: signed permission from real customers to use their marks.
 *
 * 2. "SELECTED BY 500+ TEAMS!" — the testimonial carousel. Same 500+ claim
 *    (17 users), and the quotes are attributed to people who do not exist.
 *    WOULD NEED: real, collected, attributable testimonials.
 *
 * 3. SCORE HISTORY — the third card of "Insights in Real Time", and the
 *    six-month Mar-Aug trend chart in the hero report card. Ruled out by the
 *    owner for the homepage and again here.
 *    WOULD NEED: repeat audits of the same product over months. The product
 *    stores completed_at per audit, so the data model supports it; what does
 *    not exist is the customer history to draw.
 *
 * 4. "ROI, MEASURED CLEARLY" — the whole section, a three-tab chart over
 *    Score Over Time / Cost Comparison / Fix Velocity. Every value in it is
 *    invented: score 86, friction 31, $7,400 for a manual review, 16 shipped
 *    and 2 open per sprint, and the entire Mar-Aug series. Tab 1 is Score
 *    History again. The 6M / 1Y / All range toggles are a control over data
 *    with one state, which is the defect that got the Desktop/Mobile toggle
 *    dropped.
 *    WOULD NEED: an outcome dataset the product does not collect. $49 and
 *    $3,000-$15,000 are real (pricing.py and the ruled COMPARISON_FIGURES);
 *    nothing else in the section is.
 *
 * 5. THE HERO REPORT CARD'S "+9 PTS" AND "TIME TO RESULTS 4M 12S" — a score
 *    delta is score history, and 4m 12s is a measured average nobody measured.
 *    WOULD NEED: the same audit-time query specified in docs/DECISIONS.md
 *    (median of completed_at - created_at, n and window stated, pinned).
 *
 * 6. "50+ RESEARCH-BACKED HEURISTICS" and "benchmarked against the SaaS
 *    average", from the "Not Just a Score" standfirst and its first card.
 *    An uncounted count, and the benchmark corpus that does not exist — now
 *    on its fifth surface.
 *    WOULD NEED: a heuristic register with a real count, and a scored corpus.
 *
 * 7. "+14% EST. CONVERSION LIFT" from the Sprint Roadmap preview.
 *    WOULD NEED: measured before/after conversion data from real customers.
 *
 * The artboard also labels a dimension "Onboard". That is not a copy question:
 * onboarding stopped being assessable on 2026-07-15 and
 * backend/tests/test_six_dimensions.py fails the build on it.
 *
 * ── FIXED IN PASSING ────────────────────────────────────────────────────────
 *
 * The old hand-rolled FAQ accordion mounted its answer only when open, so the
 * built /ux-auditor/index.html carried every question and NOT ONE ANSWER —
 * Google indexed the questions and none of the content under them. It uses
 * AccordionItem now, which keeps the panel in the DOM.
 */

/* AnimatedSection REMOVED 2026-08-19.
 *
 * It wrapped every section in a framer-motion div at `initial={{opacity: 0}}`,
 * revealed by `useInView`. Two reasons it goes:
 *
 * 1. NONE OF THE REBUILT PAGES USE IT. HomeLanding, MvpValidator and the four
 *    /use-cases/ pages all render flat. Keeping it here would make this the one
 *    converted page that fades.
 *
 * 2. IT PUTS REAL CONTENT AT opacity 0 AND WAITS FOR AN OBSERVER. The full-page
 *    capture of the rebuilt page came back with five blank sections, because a
 *    screenshot does not scroll and IntersectionObserver never fired. A
 *    screenshot is not a user — but "the text is there, it is just transparent
 *    until something fires" is a bet on IntersectionObserver, on JS being
 *    enabled, and on prefers-reduced-motion being handled. react-snap
 *    prerenders the markup either way, so search engines were never at risk;
 *    a reader with a blocked observer was.
 *
 * The words did not move. Only the fade is gone.
 */

/* THE CARDS LEAD WITH THE DIFFERENCE, NOT THE DELIVERABLES.
 *
 * They used to read "Basic report" against "Full detailed report", which tells
 * a buyer nothing: both are adjectives about the same noun, and the reader has
 * to take on trust that one is worth $49 more than the other. "1 page" against
 * "Up to 3 pages of the same site" is legible on sight, and it is the actual
 * structural difference between the tiers -- page_set.MAX_PAGES_FREE is 1 and
 * MAX_PAGES_PAID is 3.
 *
 * Both lists were sold from before multi-page shipped. Cross-page analysis is
 * the thing a single-page audit tool cannot produce at any price, and it was
 * on neither card.
 *
 * EVERY LINE IS TRACED TO CODE. Written down because the last set was not:
 *
 *   Up to 3 pages of the same site   page_set.py MAX_PAGES_PAID = 3; the
 *                                    same-site rule is rule 2, matched on the
 *                                    registrable domain
 *   Cross-page analysis              cross_page.py; rendered in the report and
 *                                    in the PDF, not report-only
 *   Every finding with evidence      claude_analyzer's finding schema requires
 *     and fix                        evidence{source,quote,location} and
 *                                    recommended_fix on every finding
 *   Annotated screenshots            the same schema's annotation block
 *   PDF export and sprint roadmap    report_orchestrator._extract_sprint_roadmap
 *                                    and the PDF's sprint builder
 *   Severity prioritisation          report_format._SEV_ORDER, applied at
 *                                    persist time
 *   1 page                           MAX_PAGES_FREE = 1
 *   Overall score and all 6          free_view withholds the analysis prose for
 *     dimension scores               four dimensions but keeps every score
 *   1 audit per month                pricing.PLANS["FREE"]["monthly_audits"]
 *                                    (was lifetime_audits until 2026-08-31)
 *
 * "YOUR MOST SEVERE FINDING IN FULL" IS NOT ON THE FREE CARD, and its absence
 * is deliberate. That line is live on the homepage and it is not what the code
 * does: order_findings_for_free_tier severity-sorts, then PROMOTES index 0 to
 * the highest-severity finding that is desktop AND carries a measured value. A
 * mobile finding can never take the slot (mobile is paid-only), and a measured
 * lower-severity finding outranks an unmeasured higher-severity one. The rule
 * is right -- the free tier's job is to prove we measured the site -- so the
 * copy moved rather than the code. "One finding in full" drops an ordering
 * claim the product does not honour and still says what the buyer gets.
 *
 * The Starter card carries no badge. "Best Value" stood here until 2026-08-26;
 * it was a claim about what other people bought and nothing counts that. The
 * page-count difference does the work the badge was standing in for.
 *
 * PER AUDIT, NOT ONE-TIME. Both are true and only one is useful: "one-time"
 * answers "will this recur?", which nobody asked, while "per audit" answers
 * "what do I get for $49?" and sets up the compare section honestly -- a second
 * run is a second audit, not a second charge for the same one. It also matches
 * the backend, where the field is literally named pay_per_audit. */
/* CUT OVER 2026-08-26. These were two arrays behind a `preview` flag while the
 * owner reviewed them on /ux-auditor-preview/. The review passed, so there is
 * one array again and the preview route is gone.
 *
 * NO EM DASHES IN THIS COPY. "One finding in full \u2014 evidence, impact and fix"
 * was written as an escape inside a JS string, which renders correctly, but the
 * same escape in a JSX TEXT NODE further down printed the six literal
 * characters to the page. Both are gone: the punctuation is a comma now,
 * which reads the same and cannot be got wrong twice. */
// The plan cards are PLAN_CARDS (config/planFeatures.js), the same list
// /pricing/ renders, so this page cannot show fewer plans than that one again
// (owner, 2026-10-07: it showed Free and Starter while /pricing/ showed three).

// The SIX production dimensions, in the canonical order of server._DIM_TO_FIELD.
// The cards render numbered 01-06 from this array, so the order is visible.
//
// This list said "Onboarding" and omitted Mobile UX until 2026-08-18 — the same
// false product claim that HomePage.js carried, on the page the navbar links to
// from every public route. Onboarding stopped being assessable on 2026-07-15
// because a flow cannot be audited from a single page; Mobile UX replaced it and
// is scored from a second capture at 390px. Covered now by
// backend/tests/test_six_dimensions.py.
const dimensions = [
  { name: 'Navigation', desc: 'How easily users find what they need' },
  { name: 'Visual Hierarchy', desc: 'Layout clarity and information flow' },
  { name: 'Friction Points', desc: 'Obstacles blocking user goals' },
  { name: 'Accessibility', desc: 'WCAG compliance and inclusivity' },
  { name: 'Conversion Design', desc: 'How well the UI drives actions' },
  { name: 'Mobile UX', desc: 'How the page holds up at a 390px viewport' },
];

const faqs = [
  { q: 'What types of apps can be audited?', a: 'Any web or mobile application: SaaS, e-commerce, marketplaces, fintech, healthtech, and more. Paste a URL or upload screenshots.' },
  { q: 'How accurate is the AI analysis?', a: 'Measured checks like contrast and tap targets come from the rendered page, not from estimates. Judgement calls on layout and flow are made by Claude against usability heuristics, and every finding cites its evidence.' },
  // WAS: "Most audits complete in 2-4 minutes. Complex apps with many
  // screenshots may take up to 10 minutes." Two unsourced precise figures, and
  // the "up to 10 minutes" upper bound directly contradicted the "under 5
  // minutes" commitment made on the homepage, /about, /use-cases/saas/,
  // /use-cases/startups/, /use-cases/agencies/, the blog and the audit form.
  //
  // A service commitment can stand without measured data; two commitments that
  // disagree cannot. Aligned to the one number and the caveat kept in words
  // rather than in a second, equally unsourced figure. Ruled 2026-08-19.
  { q: 'How long does an audit take?', a: 'Most audits complete in under 5 minutes. Complex apps with many screenshots can take longer.' },
  { q: 'Can I audit competitor apps?', a: 'Yes! Paste a URL or upload screenshots of any app you have access to. Great for competitive benchmarking.' },
  { q: 'Can I get a 1-on-1 expert walkthrough?', a: `Yes! Add a 30-minute Expert Consultation ($${ADDON_PRICES.expert_call.usd}) at checkout with your Starter audit. A UX specialist will personally walk you through your audit results and answer your questions.` },
];

// SEO brief, Task 4 (owner-approved 2026-10-07). The three questions below
// show only when `fresh`. The two false-claim fixes in `faqs` above are live
// now (owner, 2026-10-07: a false claim does not wait on the preview): the
// page scans a URL, so "just upload screenshots" was wrong, and we train no
// model, so "trained on thousands of UX patterns" was false.
const FRESH_FAQS = [
  ...faqs,
  { q: 'Is the website UX audit free?', a: `Yes, one free audit every month. It gives you the overall score, every dimension score and your most severe finding. The full ranked findings, quick wins and sprint roadmap come with a Starter audit at $${PLAN_PRICES.STARTER.usd}, paid once per audit. Free and Starter have no subscription.` },
  { q: 'Do I need to install anything?', a: 'No. Paste the page\'s URL. Nothing goes on your site, and there is no script or tag to add.' },
  { q: 'Does it check mobile?', a: 'Yes. Every URL audit also renders the page at 390px, and Mobile UX is scored as its own dimension.' },
];


/**
 * ROI, MEASURED CLEARLY — the artboard section, rebuilt WITHOUT the chart.
 *
 * The artboard draws this as a three-tab chart (Score Over Time / Cost
 * Comparison / Fix Velocity) over a Mar-Aug series. Every value in that series
 * is invented, tab 1 is Score History under another name, and the 6M/1Y/All
 * range toggles are a control over data with exactly one state. Ruled
 * 2026-08-20: build the COST COMPARISON only, static, two columns, no chart.
 *
 * WHICH LEAVES EXACTLY TWO FIGURES, AND BOTH ARE ALREADY RULED.
 *
 *   $3,000-$15,000   COMPARISON_FIGURES in test_pricing_copy_matches_backend
 *                    - a claim about what SOMEONE ELSE charges, which no plan
 *                      table can validate. Ruled in on 2026-08-19.
 *   $49              pricing.PLANS["STARTER"]["usd"]. A real price.
 *
 * "under 5 minutes" is the single service commitment already made on the
 * homepage, /about, three /use-cases/ pages, the blog, the audit form and this
 * page's own meta description. Reused, not re-invented.
 *
 * WHAT IS DELIBERATELY ABSENT: a turnaround NUMBER for the manual column.
 * "2-5 days" is live in HomePageV2's FAQ, so quoting it here would not be a new
 * claim so much as a fifth surface for an unsourced one. The row says "days"
 * and stops, which makes the same point and asserts nothing that needs a rate
 * card behind it.
 *
 * AND IT IS NOT A STRAWMAN. The last line concedes what a manual review does
 * that this does not - reading the product in context, talking to the team.
 * That is not a hedge added for balance; it is already this site's stated
 * position in HomePageV2's FAQ ("commission a manual expert review before major
 * redesigns or fundraising rounds"). A comparison that only a fool could lose
 * reads as one.
 */
const ROI_ROWS = [
  { label: 'Cost',          manual: '$3,000-$15,000',                       ours: `$${PLAN_PRICES.STARTER.usd} per audit. Your first audit is free.` },
  { label: 'Turnaround',    manual: 'Days, booked around a specialist\u2019s calendar', ours: 'Under 5 minutes, whenever you run it' },
  /* SCOPE, AND THE CONDITION UNDER IT.
   *
   * WAS: "All six dimensions, every single run". That was false, and falsifiable
   * by the product's own validator. Mobile UX is scored only when there is
   * evidence to score it on, and validator.has_mobile_evidence names the two
   * ways that evidence exists: a 390px capture, or the customer declaring the
   * artefact a mobile app. collector.py skips the 390px capture for Figma
   * embeds by design (the embed viewer renders a fixed desktop canvas, so a
   * phone screenshot of it proves nothing), and an upload never reaches the
   * collector at all. In both cases validator.py:708 removes the mobileUX
   * dimension outright. Five dimensions, not six, and the old cell promised
   * six to every buyer who was about to upload a Figma link.
   *
   * NOT "up to six". A ceiling tells the buyer what the worst case is and
   * leaves them guessing which case they are in. The condition tells them
   * before they choose an input, which is the only point in the funnel where
   * knowing it changes anything.
   *
   * One thing this cell still does not say: a URL run whose mobile capture
   * FAILS also drops to five (the capture is non-fatal by design). That is a
   * per-run outcome rather than a property of the input, and the report is
   * where it belongs - the dimension renders "Not assessed" rather than
   * vanishing, so the run says which case it was. A table cell cannot carry
   * a per-run truth; it can carry the condition, and does. */
  { label: 'Scope',         manual: 'Set per engagement',                   ours: 'Six dimensions on a website URL. Five on a Figma link or an upload, unless you mark it a mobile app.' },
  { label: 'Running it again', manual: 'A new engagement',                  ours: 'Re-run after any release' },
  { label: 'Consistency',   manual: 'Depends who runs it',                  ours: 'The same scoring rules on every audit' },
];

/**
 * The cross-page analysis section (1c) is unconditional as of the 2026-09-01
 * cut-over. It shipped behind `/ux-auditor-preview/` first; the owner reviewed the
 * rendered page and ruled ship, so the preview route, its prerender entry and
 * its robots Disallow were deleted in the same commit that flipped this on.
 *
 * A preview route left live is this page at a second URL, and a canonical is a
 * hint rather than a directive. The surface existed to be looked at once.
 */
export default function UxAuditorPage({ preview = false }) {
  // The P1 SEO copy: on /ux-auditor-preview/ now, on /ux-auditor/ once SEO_P1_LIVE flips.
  const fresh = preview || SEO_P1_LIVE;
  const roiRows = fresh
    ? ROI_ROWS.map((r) => (r.label === 'Cost' ? { ...r, ours: `$${PLAN_PRICES.STARTER.usd} per audit. One free audit every month.` } : r))
    : ROI_ROWS;
  const { login, user, setUser } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  const faqList = fresh ? FRESH_FAQS : faqs;
  const appSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'AI UX Auditor by UXAuditPro',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: 'https://www.uxauditpro.com/ux-auditor/',
    description: 'AI-powered UX audit tool. Audit up to 3 pages of one site across 6 UX dimensions, with a cross-page comparison, in under 5 minutes.',
    brand: { '@type': 'Brand', name: 'UXAuditPro' },
    // THE OFFER DESCRIPTIONS ARE THE CARDS' FIRST LINE, not a second
    // wording of them. "1 audit lifetime" against "Full detailed report"
    // compared a quota to an adjective; the page count is the difference
    // both surfaces should state, and structured data drifting from the
    // visible page is the failure this block already had once.
    //
    // THE PRICES ARE STRINGS BECAUSE SCHEMA.ORG WANTS STRINGS, but they
    // are built from PLAN_PRICES rather than typed. `price: '49'` was a
    // bare literal until 2026-08-31 — the single worst place in the
    // codebase for one, because a stale price in structured data is what
    // Google shows in a result. Nobody reads it in review, and it does
    // not look wrong; it just stops being true.
    offers: [
      { '@type': 'Offer', name: 'Free', price: String(PLAN_PRICES.FREE.usd), priceCurrency: 'USD', description: `1 page, ${PLAN_PRICES.FREE.quota}` },
      { '@type': 'Offer', name: 'Starter', price: String(PLAN_PRICES.STARTER.usd), priceCurrency: 'USD', description: 'Up to 3 pages of the same site, per audit' },
    ],
    // Structured data is a product claim in machine-readable form, and
    // this list shipped "Onboarding evaluation" to search engines for a
    // dimension the product stopped scoring on 2026-07-15.
    //
    // THE FIRST SIX ARE STILL THE SIX DIMENSIONS, in the `dimensions`
    // array's order, and that invariant is why they stay first. The two
    // after them are not dimensions and do not pretend to be: they are
    // what the paid tier adds ACROSS pages, which is a different axis and
    // was missing from the machine-readable claim exactly as it was
    // missing from the visible one.
    featureList: [
      'Navigation analysis',
      'Visual hierarchy scoring',
      'Friction point detection',
      'Accessibility compliance',
      'Conversion design review',
      'Mobile UX scoring from a 390px capture',
      'Up to 3 pages of the same site in one audit, each scored on its own',
      'Cross-page analysis of what differs, degrades or holds across those pages',
    ],
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };
  const [showCheckout, setShowCheckout] = useState(false);

  return (
    <div>
      <AuditCheckoutPopup
        visible={showCheckout}
        onClose={() => setShowCheckout(false)}
        user={user}
        setUser={setUser}
        source="pricing_page"
      />
      {/* META DESCRIPTION: "under 2 minutes" -> "under 5 minutes", 2026-08-19.
          A THIRD number for the same commitment, and the only one of the three
          that was in a meta description — the surface a searcher reads before
          they ever load the page. Found by sweeping the timing claims for
          consistency under the owner's ruling, not by reading the page.

          PREVIEW ROUTE, 2026-08-20: noindex AND self-canonical. noindex plus a
          canonical pointing at /ux-auditor/ would be two contradictory signals
          about one URL; self-canonical plus noindex says one thing. It is also
          kept out of public/sitemap.xml. */}
      <SEO
        fullTitle={fresh ? 'Free Website UX Audit Tool: Scan Any URL | UXAuditPro' : 'Website UX Auditor: Scan Any URL in Seconds | UXAuditPro'}
        description={fresh
          ? 'Paste any URL for an instant website UX audit. AI scores usability, accessibility and conversion across 6 dimensions. One free audit every month.'
          : 'Paste any URL and our AI UX auditor analyses usability, accessibility, conversion paths, and more. Get a prioritised report in under 5 minutes. Free to try.'}
        noIndex={preview}
        canonical="/ux-auditor"
        ogTitle="Website UX Auditor — Scan Any URL | UXAuditPro"
        structuredData={fresh ? [appSchema, faqSchema] : appSchema}
      />
      {/* Hero */}
      {/* ═══ HERO ══════════════════════════════════════════════════════════════
          The artboard's shape: centred type over the grid wash, then the report
          card floating beneath it. Every string is the live page's own. */}
      <header className="uxa-pt-xl px-6 hero" style={{ position: 'relative', textAlign: 'center' }}>
        <div className="uxa-hero-grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 1180, margin: '0 auto' }}>
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full"
            style={{ backgroundColor: 'var(--accent-tint)', border: '1px solid var(--border-2)', fontSize: 'var(--type-dense-size)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent-text)' }}
          >
            <Scan className="w-3.5 h-3.5" aria-hidden="true" />
            AI UX Auditor
          </span>
          <h1 className="uxa-h1" style={{ color: 'var(--ink-1)', maxWidth: 900, margin: '0 auto' }}>
            {fresh ? (
              <>Website{' '}<span style={{ color: 'var(--accent-display)', fontStyle: 'italic', fontWeight: 700 }}>UX Audit Tool</span></>
            ) : (
              <>Instant UX audit —{' '}<br /><span style={{ color: 'var(--accent-display)', fontStyle: 'italic', fontWeight: 700 }}>Paste your URL</span>, get insights</>
            )}
          </h1>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', maxWidth: 620, margin: '20px auto 0' }}>
            Upload screenshots of your app and receive an AI-powered audit across 6 critical UX dimensions. Scored, prioritized, actionable.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 34 }}>
            {user ? (
              <Link to="/audit/new" data-testid="ux-hero-cta" className="uxa-submit uxa-submit--auto">
                Audit My App
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            ) : (
              <button onClick={login} data-testid="ux-hero-cta" className="uxa-submit uxa-submit--auto">
                Audit My App Free
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ═══ THE REPORT CARD ═══════════════════════════════════════════════════
          REAL NUMBERS, from a real audit — the same fixture the
          homepage report card reads, so the two cannot drift apart. Overall 58,
          six dimension scores, 4 findings (0 critical, 3 major, 1 minor).

          The artboard draws three stat cells here: Overall Score 72 with a
          "+9 pts" delta, Findings 18 with "3 critical", and Time to Results
          "4m 12s". Two of the three are omitted and logged in the header block:
          a delta is score history, and 4m 12s is an average nobody measured.
          The severity row shows only the severities this audit actually has —
          an absent row says zero more clearly than a loud badge saying zero. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-report-panel">
          <div className="uxa-report-head">
            <div>
              <p className="uxa-report-label">Report</p>
              <div style={{ fontSize: 'var(--type-h3-size)', fontWeight: 700, margin: '6px 0 0' }}>{DEMO_AUDIT.appName}</div>
              <p style={{ fontSize: 'var(--type-dense-size)', color: 'var(--ink-2)', margin: '2px 0 0' }}>{DEMO_AUDIT.url}</p>
            </div>
          </div>
          <div className="uxa-report-grid">
            <div className="uxa-report-panel" style={{ background: 'var(--surface-white)' }}>
              <div className="uxa-score-row">
                <ScoreRing score={DEMO_AUDIT.overall} size={132} />
                <div>
                  <p className="uxa-report-label">Findings</p>
                  <div style={{ fontSize: 34, fontWeight: 700, letterSpacing: '-0.04em', margin: '4px 0 0' }}>
                    {DEMO_AUDIT.severityCounts.critical + DEMO_AUDIT.severityCounts.major + DEMO_AUDIT.severityCounts.minor}
                  </div>
                  <p style={{ fontSize: 'var(--type-dense-size)', color: 'var(--ink-2)', margin: '2px 0 0' }}>
                    {DEMO_AUDIT.severityCounts.major} major · {DEMO_AUDIT.severityCounts.minor} minor
                  </p>
                </div>
              </div>
            </div>
            <div className="uxa-report-panel" style={{ background: 'var(--surface-white)' }}>
              <p className="uxa-report-label">Dimension Scores</p>
              <div style={{ marginTop: 18 }}>
                <DimensionBars scores={DEMO_AUDIT.scores} height={104} testId="uxauditor-dimension-bars" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      {/* ═══ HOW IT WORKS ══════════════════════════════════════════════════════ */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
          <span className="section-label">How It Works</span>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>Three steps to a professional UX audit</h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>Upload, analyze, fix.</p>
        <div className="uxa-grid-3" style={{ marginTop: 56 }}>
          {[
            { icon: Upload, title: 'Upload screenshots', desc: 'Drag and drop screenshots of your app, paste a URL, or connect your Figma file.' },
            // WAS: "Claude Vision AI examines every pixel across 6 UX dimensions
            // in under 4 minutes." The fifth surface of the item-3 claim, cut
            // 2026-08-19: "every pixel" writes the DOM-computed measurements out
            // of the description of a pipeline whose strongest evidence they
            // are, and "under 4 minutes" contradicted the "under 5 minutes"
            // commitment stated in fourteen other places.
            { icon: BarChart3, title: 'AI analyzes', desc: 'AI reviews every screen you give it and scores 6 UX dimensions.' },
            { icon: FileText, title: 'Get your report', desc: 'Receive scored findings with severity ratings, fix recommendations, and a PDF download.' },
          ].map((step, i) => (
              <div className="uxa-feature-card">
                <span style={{ width: 52, height: 52, borderRadius: 'var(--radius-md)', background: 'var(--accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <step.icon className="w-6 h-6" style={{ color: 'var(--accent-text)' }} aria-hidden="true" />
                </span>
                <h3 className="uxa-h3" style={{ color: 'var(--ink-1)', margin: '24px 0 8px' }}>{step.title}</h3>
                <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{step.desc}</p>
              </div>
          ))}
        </div>
      </section>

      {/* ═══ THE SIX DIMENSIONS ════════════════════════════════════════════════
          The artboard's "Not Just a Score. A Diagnosis." slot, carrying the live
          page's own "The Score / 6 UX dimensions" content. The dark panel is the
          page's one tonal break, as on the homepage.

          The artboard's standfirst claimed "50+ research-backed heuristics" and
          its first card said the scores are "benchmarked against the SaaS
          average". Both omitted — an uncounted count, and a benchmark corpus
          that does not exist. See the header block. The artboard also labels a
          bar "Onboard"; that is a build failure, not a copy question. */}
      <section className="uxa-landing-section uxa-pt-xl" id="use-cases">
        <div className="uxa-dark-panel">
          <div style={{ textAlign: 'center' }}>
            <span className="uxa-dark-panel__eyebrow">The Score</span>
            <h2 className="uxa-h2" style={{ color: 'var(--text-inverse)', margin: '20px auto 0', maxWidth: 760 }}>6 UX dimensions</h2>
            <p className="uxa-lead" style={{ color: 'var(--ink-4)', margin: '16px auto 0', maxWidth: 620 }}>
              Every audit scores your product across these six areas
            </p>
          </div>
          <div className="uxa-dim-grid">
            {dimensions.map((dim, i) => (
              <div key={dim.name} className="uxa-dim-grid__cell">
                <span className="uxa-dim-grid__mark" aria-hidden="true">{`0${i + 1}`}</span>
                <h3 className="uxa-h3" style={{ color: 'var(--text-inverse)', margin: '0 0 8px' }}>{dim.name}</h3>
                <p className="uxa-lead" style={{ color: 'var(--ink-4)', margin: 0 }}>{dim.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHAT THE WEBSITE UX AUDIT CHECKS (SEO brief, Task 4) ═════════════
          Owner-approved copy, 2026-10-07, behind `fresh` with the rest of the
          P1 copy. Every measured check is a rule in backend/audit/element_keys.py
          (RULES) or the generic-link list in claude_analyzer.py; the two widths
          are collector.py's 1440x900 desktop and 390px mobile contexts. */}
      {fresh && (
        <section className="uxa-landing-section uxa-pt-xl" data-testid="auditor-checks">
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>What the website UX audit checks</h2>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px 0 0' }}>
              Paste a URL and we load the page twice: once in a 1440px desktop window and once at 390px, the width of a typical phone. Some things are measured from the rendered page itself. The rest are judged from the two screenshots against established usability heuristics.
            </p>
            <h3 className="uxa-h3" style={{ color: 'var(--ink-1)', margin: '40px 0 16px' }}>Measured on the page</h3>
            <ul className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0, paddingLeft: 20 }}>
              <li style={{ margin: '0 0 12px' }}><strong style={{ color: 'var(--ink-1)' }}>Text contrast.</strong> Sampled text is checked against WCAG AA: 4.5:1 for normal text, 3:1 for large text. A finding quotes the exact colours and the ratio.</li>
              <li style={{ margin: '0 0 12px' }}><strong style={{ color: 'var(--ink-1)' }}>Text size.</strong> Font sizes are read from the page, never guessed from a screenshot.</li>
              <li style={{ margin: '0 0 12px' }}><strong style={{ color: 'var(--ink-1)' }}>Tap targets.</strong> On the 390px render, buttons and links smaller than 44 by 44 pixels are flagged, and so are pairs closer than 8px, where a thumb lands on the wrong one. Inline text links are exempt.</li>
              <li style={{ margin: '0 0 12px' }}><strong style={{ color: 'var(--ink-1)' }}>Images without alt text.</strong> Images with no alt attribute are listed. An empty alt on a decorative image is correct, so it is left alone.</li>
              <li style={{ margin: '0 0 12px' }}><strong style={{ color: 'var(--ink-1)' }}>Mobile layout.</strong> Whether the page scrolls sideways at 390px, and whether it has a responsive viewport tag.</li>
              <li style={{ margin: '0 0 12px' }}><strong style={{ color: 'var(--ink-1)' }}>Content that disappears on mobile.</strong> Labels on the desktop render that are missing from the phone render, checked against the screenshots so a link tucked into a menu is not counted as lost.</li>
              <li style={{ margin: '0 0 12px' }}><strong style={{ color: 'var(--ink-1)' }}>A call to action in the first screen.</strong> Whether the first screen, at both widths, has one clear action outside the navigation.</li>
              <li style={{ margin: '0 0 12px' }}><strong style={{ color: 'var(--ink-1)' }}>Vague link text.</strong> Links that say only &quot;click here&quot;, &quot;learn more&quot; or &quot;read more&quot;.</li>
            </ul>
            <h3 className="uxa-h3" style={{ color: 'var(--ink-1)', margin: '40px 0 16px' }}>Judged from the screenshots</h3>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>
              Navigation: how easily a visitor finds the main paths. Visual Hierarchy: whether the layout leads the eye to what matters first. Friction Points: steps that slow people down or make them hesitate. Conversion Design: whether the next action is obvious and worth taking. Accessibility and Mobile UX draw on both the measurements and the screenshots.
            </p>
            <h3 className="uxa-h3" style={{ color: 'var(--ink-1)', margin: '40px 0 16px' }}>What you get back</h3>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>
              Every audit gives an overall score out of 100 and a score for each of the six dimensions. Your free audit each month adds your most severe finding. A Starter audit, ${PLAN_PRICES.STARTER.usd} paid once, adds every finding ranked by impact with a fix for each, the quick wins and a three-sprint roadmap.
            </p>
          </div>
        </section>
      )}

      {/* ═══ WHAT THE REPORT CONTAINS ══════════════════════════════════════════
          The artboard's four-card "Not Just a Score. A Diagnosis." row. The
          previews are FeaturePreview, which the homepage already uses — small,
          static, and made only of things this product actually produces. The
          Sprint Roadmap preview in the artboard carries "+14% est. conversion
          lift"; FeaturePreview's version has no such figure, which is why it is
          used rather than reproduced. */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
          <span className="section-label">What You Get</span>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>Not just a score. A diagnosis.</h2>
        <div className="uxa-grid-3" style={{ marginTop: 56 }}>
          {[
            { kind: 'score', title: 'Six Dimension Scores', desc: 'See how your product performs on navigation, visual hierarchy, friction points, accessibility, conversion design and mobile UX.' },
            { kind: 'wins', title: 'Quick Wins', desc: 'Low-effort, high-impact fixes you can ship in the current sprint.' },
            { kind: 'sprints', title: 'Sprint Roadmap', desc: 'A prioritised backlog split across three sprints, ranked by impact and effort.' },
          ].map((f, i) => (
              <div className="uxa-feature-card">
                <div className="uxa-feature-card__bloom" aria-hidden="true" />
                <FeaturePreview kind={f.kind} />
                <h3 className="uxa-h3" style={{ margin: '24px 0 8px' }}>{f.title}</h3>
                <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{f.desc}</p>
              </div>
          ))}
        </div>
      
          {/* The grid above enumerates what a report CONTAINS -- six dimension
              scores, quick wins, a sprint roadmap. The sample report is one,
              filled in. Same link, same words, same tertiary skin as the
              homepage and /case-studies/. Held until that page's record is a
              real audit -- SAMPLE_REPORT_PUBLISHED. */}
          {SAMPLE_REPORT_PUBLISHED && (
            <p className="uxa-sample-link">
              <Link className="uxa-submit uxa-submit--auto uxa-submit--tertiary" to="/sample-report/">
                {SAMPLE_REPORT_LINK_TEXT}
              </Link>
            </p>
          )}
        </section>

      {/* ═══ INSIGHTS IN REAL TIME ═════════════════════════════════════════════
          The artboard draws three cards here. TWO ARE BUILT.

          "Desktop & Mobile Capture" is built to describe what the pipeline
          actually does, per the owner's ruling: collector.py takes a desktop and
          a 390px mobile capture, and Mobile UX is a scored dimension in its own
          right rather than an average.

          "Evidence-Checked Findings" is true as drawn — claude_analyzer.py
          forbids eyeballed text sizes outright and requires the DOM-measured
          value on any finding that judges legibility.

          "SCORE HISTORY" IS OMITTED. Ruled out by the owner, here and on the
          homepage. It would need repeat audits of the same product over months.
          The artboard's standfirst for this section ("Re-run the audit after
          every release and watch the score move") is the same promise in prose,
          so it is not reproduced either. */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
          <span className="section-label">How It Reads Your Product</span>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>Measured, not estimated</h2>
        <div className="uxa-grid-2" style={{ marginTop: 56 }}>
          {[
            { icon: Smartphone, title: 'Desktop & Mobile Capture', desc: 'Your page is captured twice — once on desktop and once at a 390px viewport — and Mobile UX is scored as its own dimension, so mobile problems never hide inside a desktop average.' },
            { icon: ShieldCheck, title: 'Evidence-Checked Findings', desc: 'Contrast ratios and tap-target sizes come from the DOM, not from looking at a screenshot. A finding that judges text size has to carry the measured value or it is dropped.' },
          ].map((f, i) => (
              <div className="uxa-feature-card" style={{ display: 'flex', gap: 22, textAlign: 'left' }}>
                <span style={{ width: 52, height: 52, flex: 'none', borderRadius: 'var(--radius-md)', background: 'var(--accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <f.icon className="w-6 h-6" style={{ color: 'var(--accent-text)' }} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="uxa-h3" style={{ margin: '0 0 8px' }}>{f.title}</h3>
                  <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{f.desc}</p>
                </div>
              </div>
          ))}
        </div>
      </section>

      {/* ═══ THREE PAGES, AND WHAT RUNS BETWEEN THEM ═══════════════════════════
          NEW SECTION, 2026-08-26. Nothing above or below it moved.

          Both marketing pages were selling the product as it existed before
          multi-page shipped. The paid tier covers three pages and runs a
          comparison across them, and neither fact appeared anywhere on either
          page -- not in the cards, not in the FAQ, not in the structured data.
          The single biggest reason to pay was invisible at the point of sale.

          IT IS A NEW SECTION RATHER THAN A REWRITE because content preservation
          forbids rewording what is already here, and because the cards cannot
          carry it: "Up to 3 pages of the same site" is a good line on a card
          and a bad explanation of what the third analysis pass actually does.

          Placed after "Measured, not estimated" and before the compare section,
          which is the order the reader needs: how one page is read, then what
          three pages add, then what a second run adds.

          THE THIRD CARD IS THE LIMITS, and it is not modesty. Every line in it
          is a refusal the code actually makes -- the same-site rule, URL-only
          input, soft per-page failure. A reader who finds out at the form that
          a screenshot upload cannot be multi-page has been sold something else. */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl" data-testid="multi-page-section">
          <span className="section-label">Multi-Page</span>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>Three pages, and what runs between them</h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>
            A paid audit covers up to three pages of the same site. Each is scored on its own, and a
            third pass looks only at what is true between them.
          </p>
        <div className="uxa-grid-3" style={{ marginTop: 56 }}>
          {[
            {
              icon: Layers,
              title: 'Every page scored on its own',
              desc: 'Pages two and three go through the same six-dimension analysis as the first, each with its own score, its own findings and its own evidence. There is no site-wide average, deliberately: an average of three pages describes none of them, so every number stays attached to the page it came from.',
            },
            {
              icon: Network,
              title: 'Findings that only exist across pages',
              desc: 'The comparison pass reports relationships rather than repeats. What differs between your pages, which page is the outlier in a dimension, what holds consistently across all of them, and what degrades along a path, the way form fields can climb from one step to the next. A claim that names only one page is discarded before you see it.',
            },
            {
              icon: Compass,
              title: 'What it will not do',
              desc: 'Every page has to belong to the same site: a conclusion about "the site" drawn across two unrelated domains is not so much wrong as meaningless. Multi-page needs URLs, so a screenshot upload is a one-page audit. And if a page fails to capture, you lose that page\u2019s section and the report says so, never the whole audit.',
            },
          ].map((f) => (
              <div className="uxa-feature-card" key={f.title} style={{ textAlign: 'left' }}>
                <span style={{ width: 52, height: 52, flex: 'none', borderRadius: 'var(--radius-md)', background: 'var(--accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 22 }}>
                  <f.icon className="w-6 h-6" style={{ color: 'var(--accent-text)' }} aria-hidden="true" />
                </span>
                <h3 className="uxa-h3" style={{ margin: '0 0 8px' }}>{f.title}</h3>
                <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{f.desc}</p>
              </div>
          ))}
        </div>
      </section>

      <CrossPageAnalysis />

      {/* ═══ COMPARE TWO RUNS ══════════════════════════════════════════════════
          The honest half of what SCORE HISTORY was reaching for.

          Score History is ruled out on this page and on the homepage, and the
          section above declines to reproduce the artboard's standfirst for it
          ("Re-run the audit after every release and watch the score move").
          That standfirst promises a series over time. THIS SECTION PROMISES A
          PAIR, which is the thing the product actually builds: /audits/compare
          takes exactly two audits and diffs them.

          RULED 2026-08-24, narrow framing. Everything below is a statement
          about behaviour that exists in code, not about outcomes nobody has
          measured:

            * dimensions ordered by absolute delta, unassessed excluded and
              labelled           - audit/compare.py::_compare_dimensions
            * findings split resolved / new / persistent, each new one
              carrying its recommended fix
                                 - audit/compare.py::_diff_findings,
                                   _finding_detail
            * the pair re-ordered by created_at so the older run is the
              baseline           - server.compare_audits, `swapped`

          WHAT IS DELIBERATELY ABSENT, and why:

            * NO TREND, NO TIME SERIES, NO CHART. Two runs is a pair. A third
              run does not make a line here; the page compares two.
            * NO CLAIM ABOUT TYPICAL IMPROVEMENT. "Teams gain N points on the
              second audit" would need an outcome dataset the product does not
              collect - the same reason ROI's Score Over Time tab was cut.
            * NO IMPLICATION THAT FREE REACHES IT. FREE is one audit per month
              (pricing.PLANS), so a comparison needs a second, paid run. The
              closing line says the price rather than letting the section read
              as a free feature.

          No CTA: /audits/compare is behind ProtectedRoute, so a link from a
          public marketing page is a login wall for most of its readers. */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl" data-testid="compare-runs-section">
          <span className="section-label">After The Fix</span>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>Audit the same page again, and compare the two</h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>
            A second audit of the same page lines up against the first. Two runs, side by side.
          </p>
        <div className="uxa-grid-2" style={{ marginTop: 56 }}>
          {[
            { icon: BarChart3, title: 'Every dimension, side by side', desc: 'Both scores sit on one row, ordered by how far each dimension moved rather than by name. A dimension only one of the two runs could score reads "Not assessed" and stays out of the arithmetic, so a missing reading is never counted as a drop.' },
            { icon: CheckCircle, title: 'What you fixed, and what is new', desc: 'Findings split three ways: resolved, new, and still open. Each new issue carries the same recommended fix the full report gives it, so the comparison tells you what to do and not only what changed.' },
          ].map(f => (
              <div className="uxa-feature-card" key={f.title} style={{ display: 'flex', gap: 22, textAlign: 'left' }}>
                <span style={{ width: 52, height: 52, flex: 'none', borderRadius: 'var(--radius-md)', background: 'var(--accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <f.icon className="w-6 h-6" style={{ color: 'var(--accent-text)' }} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="uxa-h3" style={{ margin: '0 0 8px' }}>{f.title}</h3>
                  <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{f.desc}</p>
                </div>
              </div>
          ))}
        </div>
        {/* REFRAMED 2026-08-26. It read: "your free audit is the first, and a
            second run is $49" -- which puts a price immediately after the name
            of a feature and reads as a charge for the comparison. There is no
            such charge: the compare endpoint has no entitlement check of any
            kind, so any two audits of the same page can be compared, free ones
            included.

            The 2026-08-24 ruling that put a price here still holds and is not
            being reversed: FREE is one audit per month, so a comparison cannot
            be reached on the free tier alone, and letting this section read as
            a free feature would be the opposite lie. What changes is WHAT the
            price is attached to. A second audit costs what an audit costs. The
            comparison costs nothing, because it is not a product. */}
        <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '32px auto 0', maxWidth: 720 }}>
          We order the two by date, so the earlier run is always the baseline, whichever way round you
          pick them. Comparing them costs nothing on its own: it comes with any two finished audits of
          the same page. What you actually need is the second audit. Your free audit covers the first,
          and every audit after that is ${PLAN_PRICES.STARTER.usd}.
        </p>
      </section>

      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl" data-testid="ai-methodology-section">
        <div>
            <span className="section-label">Methodology</span>
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>Powered by 3 AI models</h2>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>Our unique methodology combines three analytical lenses for comprehensive UX analysis</p>
          <div className="uxa-grid-3" style={{ marginTop: 56 }}>
            {[
              { name: 'Heuristic Engine', icon: Brain, color: 'var(--accent-text)', bg: 'var(--surface-panel)', items: ['Visibility of system status', 'User control and freedom', 'Consistency and standards', 'Error prevention & recovery'] },
              { name: 'Behavioral Lens', icon: Eye, color: 'var(--accent-text)', bg: 'var(--surface-panel)', items: ['F-pattern & Z-pattern scanning', 'Cognitive load optimization', "Click target sizing (Fitts's law)", 'Visual hierarchy flow'] },
              // --success and #F0FDF4 out: tokens.css scopes --success to "form
              // validation only. NEVER a score", and #F0FDF4 is not a token at
              // all. All three lenses now sit on the same card ground, told
              // apart by name and icon rather than by a colour the system does
              // not own — the same call made on /use-cases/.
              { name: 'Conversion Optimizer', icon: Target, color: 'var(--accent-text)', bg: 'var(--surface-panel)', items: ['CTA effectiveness', 'Trust signals & social proof', 'Friction reduction', 'Value proposition clarity'] },
            ].map((ai, i) => (
                <div className="uxa-feature-card" style={{ textAlign: 'left' }}>
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: 'var(--surface-white)', border: '1px solid var(--border-2)' }}>
                    <ai.icon className="w-7 h-7" style={{ color: ai.color }} />
                  </div>
                  <h3 className="uxa-h3 mb-3" style={{ color: 'var(--ink-1)' }}>{ai.name}</h3>
                  <ul className="space-y-2 text-sm">
                    {ai.items.map(item => (
                      <li key={item} className="flex items-center gap-2" style={{ color: 'var(--ink-2)' }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ai.color }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl" id="pricing" data-testid="ux-pricing-section">
        <div>
            <span className="section-label">Pricing</span>
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>Simple, transparent pricing</h2>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>Start free. Upgrade when you need more.</p>
          <div className="uxa-grid-3" style={{ marginTop: 56, alignItems: 'start' }}>
            <PlanCards onCheckout={() => setShowCheckout(true)} />
          </div>
        </div>
      </section>

      {/* ═══ ROI, MEASURED CLEARLY ═════════════════════════════════════════
          CUT OVER 2026-08-26 with the pricing rewrite, on the owner's word
          after reviewing the preview. It was preview-only before that.

          THE $3,000-$15,000 RANGE GOES LIVE WITH IT. That figure is owner-ruled
          (2026-08-19, recorded in COMPARISON_FIGURES) and has no rate card
          behind it, which is why the guard carries it as a named exception
          rather than a pattern. Nothing about the cut-over re-opens that.

          Two cards rather than a <table>: the rows are five label/value pairs
          per side, and a real table at 390px either scrolls sideways or
          collapses into exactly these two stacks anyway. <dl> keeps the
          label/value relationship for a screen reader without the layout debt.

          The manual column carries NO accent. Colour-coding the comparison
          would make the ground do the arguing; the words already do it. */}
        <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl" data-testid="roi-section">
          <span className="section-label">ROI</span>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>ROI, measured clearly</h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>
            A manual expert review costs $3,000-$15,000 and takes days. This runs in under five minutes, on the scope set out below.
          </p>
          <div className="uxa-grid-2" style={{ marginTop: 56, alignItems: 'stretch' }}>
            {[
              { who: 'Manual expert review', key: 'manual', accent: false },
              { who: 'UXAuditPro', key: 'ours', accent: true },
            ].map((col) => (
              <div
                key={col.key}
                className="uxa-feature-card"
                style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}
              >
                <h3 className="uxa-h3" style={{ color: 'var(--ink-1)', margin: '0 0 4px' }}>{col.who}</h3>
                {/* The rule under the column head is the one accent mark in the
                    section, and it carries no text, so it is #FF5C2B and not
                    the text-safe #B3320A. */}
                <span
                  aria-hidden="true"
                  style={{ display: 'block', width: 48, height: 3, borderRadius: 2, marginBottom: 28, background: col.accent ? 'var(--accent)' : 'var(--border-2)' }}
                />
                <dl style={{ margin: 0 }}>
                  {roiRows.map((row, i) => (
                    <div
                      key={row.label}
                      /* The last row carries no rule. A border on every row put
                         a trailing line under "Consistency" with only the card's
                         padding beneath it, which reads as a row that failed to
                         render rather than as the end of a list. */
                      style={{
                        display: 'flex', flexDirection: 'column', gap: 4,
                        paddingBottom: i === roiRows.length - 1 ? 0 : 18,
                        marginBottom: i === roiRows.length - 1 ? 0 : 18,
                        borderBottom: i === ROI_ROWS.length - 1 ? 'none' : '1px solid var(--border-1)',
                      }}
                    >
                      {/* --ink-2, NOT --ink-3. #8B8883 measures 3.53:1 on the
                          white card, an AA failure at 14px — the same defect
                          .section-label was moved off in af017d5. */}
                      <dt style={{ fontSize: 'var(--type-dense-size)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-2)' }}>
                        {row.label}
                      </dt>
                      <dd style={{ margin: 0, fontSize: 'var(--type-body-size)', lineHeight: 1.5, color: 'var(--ink-1)' }}>
                        {row[col.key]}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '32px auto 0', maxWidth: 640 }}>
            A manual review still reads your product in context and talks to your team, which this does not. Run this before every release; commission one before a redesign.
          </p>
        </section>

      {/* ═══ FAQ ═══════════════════════════════════════════════════════════════
          ON AccordionItem NOW, AND THAT IS A FIX RATHER THAN A RESTYLE.

          The old markup rendered the answer only when `openFaq === i`, so the
          prerendered /ux-auditor/index.html contained all five questions and
          NOT ONE ANSWER. Google indexed the questions and none of the content
          under them. AccordionItem keeps the panel mounted and hides it with
          `hidden`, which is what the homepage FAQ already does — the built
          /index.html carries its answers.

          Same family as the homepage audience panels: a control that hides
          content from a reader hides it from a crawler too, and an SEO diff
          cannot see what was never in the DOM to begin with. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <span className="section-label">FAQ</span>
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px 0 0' }}>Questions &amp; answers</h2>
          <div style={{ marginTop: 40 }}>
            {faqList.map((faq, i) => (
              <AccordionItem
                key={faq.q}
                question={faq.q}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              >
                {faq.a}
              </AccordionItem>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CLOSING CTA ═══════════════════════════════════════════════════════
          The artboard's "Fix the UX That Costs You Users" band, carrying this
          page's own heading and copy. Light closing band, masked grid, bloom
          under the action — the page's one tonal break is the six-dimensions
          panel above. The signed-in fork is inlined at the call site, never
          wrapped in a local component. */}
      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>Ready to audit your app?</h2>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '18px auto 0', maxWidth: 520 }}>{fresh ? 'One free audit every month. No credit card required.' : 'Get your first UX audit completely free. No credit card required.'}</p>
            <div style={{ position: 'relative', display: 'inline-block', marginTop: 40, isolation: 'isolate' }}>
              <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
                {user ? (
                  <Link to="/audit/new" data-testid="ux-bottom-cta" className="uxa-submit uxa-submit--auto">
                    Start New Audit
                    <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                  </Link>
                ) : (
                  <button onClick={login} data-testid="ux-bottom-cta" className="uxa-submit uxa-submit--auto">
                    Audit My App Free
                    <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                  </button>
                )}
              </span>
            </div>
        </div>
      </section>
    </div>
  );
}
